import type { EditKind } from "./graduation";
import { figuresDiffer, verifierFigureSet } from "./figures";
import type { Scenario } from "./scenario";

/**
 * W4 SIGNALS (Agent Brains 13.4 and 13.6, B3, B7, B15): correction-to-stage
 * attribution. When the owner corrects or rejects a draft, which STAGE of the
 * run made the mistake? Answered over the draft's own execution trace by six
 * fixed rules, never by a model (CLAUDE.md section 4: the model does not
 * grade itself; the less LLM in the loop the less drift).
 *
 * THE DISCIPLINE. Exactly one rule matches: that rule names the stage. Zero
 * matches, or two or more, is AMBIGUOUS: no lesson is written, the draft goes
 * on the human triage list (precision beats recall, CLAUDE.md section 0: a
 * wrongly attributed lesson spends client trust, a missed one costs almost
 * nothing). Two rules that happen to name the same stage are still two
 * matches: the trace did not single out one cause, so none is named.
 *
 * Cosmetic edits (F-05) yield nothing. The owner's substantive flag overrides
 * the metric either way, as it does for graduation. An approved draft whose
 * edit kind was never recorded is not read as unedited: it is ambiguous.
 *
 * R2 names the KB, and a KB gap is a ticket for the owner, never a lesson for
 * an agent (`lesson: false`): the agent did what its facts said.
 *
 * Pure: no clock, no I/O, no model. The loader (`agents/signalAttribute.ts`)
 * builds the trace from the ledger and the drafts row.
 */

/** The stages of a run an attribution can name. */
export const TRACE_STAGES = ["research", "routing", "retrieval", "compose", "gate", "kb"] as const;
export type TraceStage = (typeof TRACE_STAGES)[number];

/** A figure the owner corrected the draft TO, and the active KB facts that state it. */
export interface FigureCorrection {
  /** Ids of active KB facts whose text states the corrected figure; empty when none does. */
  readonly kbFactIds: readonly string[];
}

/** Everything the six rules read, as codes, ids and booleans. No customer or shop text. */
export interface DraftTrace {
  readonly draftId: string;
  readonly status: "approved" | "rejected";
  /** The stored F-05 classification; null when never recorded (a rejection records none). */
  readonly editKind: EditKind | null;
  /** The owner's one-tap substantive flag, when he used it. */
  readonly ownerFlaggedSubstantive: boolean | null;
  /** The KB ids the draft cited (`drafts.facts_used`). */
  readonly factsUsed: readonly string[];
  /** Figures the approved text states that the draft did not, with the KB facts that state each. */
  readonly corrected: readonly FigureCorrection[];
  /** The approved text makes a different numeric claim than the draft (added, changed or removed). */
  readonly figuresChanged: boolean;
  /** The FIRST attempt was refused for a figure violation (F-25 first pass). */
  readonly firstPassFigureRefusal: boolean;
  /** The verifier passed the stored body (the retry, when there was one). */
  readonly finalVerifierOk: boolean;
  /** AI-tell rule names the linter recorded on the draft that no longer fire on the approved text. */
  readonly aiTellsRemoved: readonly string[];
  /** The scenario routed for the turn, and the one a later routing on the thread chose; null when unchanged or unknown. */
  readonly rerouted: { readonly from: Scenario; readonly to: Scenario } | null;
  /** A `bounce.recorded` on the run's lead card producer is on the ledger for this run. */
  readonly leadCardBounced: boolean;
}

export type CorrectionAttribution =
  | { readonly draftId: string; readonly stage: TraceStage; readonly ruleId: string; readonly lesson: boolean }
  | { readonly draftId: string; readonly ambiguous: readonly TraceStage[]; readonly matched: readonly string[] }
  | { readonly draftId: string; readonly none: "no_correction" };

export interface AttributionRule {
  readonly id: string;
  readonly stage: TraceStage;
  /** False for a rule whose finding is a ticket for the owner, not a lesson. */
  readonly lesson: boolean;
  readonly matches: (t: DraftTrace) => boolean;
}

/** The violations a figure refusal is made of (verifier.ts `VerifyViolation`, the numeric ones). */
export const FIGURE_VIOLATIONS: readonly string[] = [
  "ungrounded_number",
  "stale_price",
  "memory_sourced_price",
  "cumulative_concession",
];

const citedAny = (t: DraftTrace, ids: readonly string[]): boolean => ids.some((id) => t.factsUsed.includes(id));

/** The six rules, in order. Order is the report order only; every rule is evaluated. */
export const ATTRIBUTION_RULES: readonly AttributionRule[] = [
  {
    // R1: the owner changed a figure to one the active KB holds, and the draft did not cite that fact: retrieval missed it.
    id: "R1",
    stage: "retrieval",
    lesson: true,
    matches: (t) => t.corrected.some((c) => c.kbFactIds.length > 0 && !citedAny(t, c.kbFactIds)),
  },
  {
    // R2: the corrected figure is in no KB fact: the shop never told us. A gap ticket, no lesson.
    id: "R2",
    stage: "kb",
    lesson: false,
    matches: (t) => t.corrected.some((c) => c.kbFactIds.length === 0),
  },
  {
    // R3: the first pass was refused on a figure, the retry passed, and the owner still changed a figure.
    id: "R3",
    stage: "gate",
    lesson: true,
    matches: (t) => t.status === "approved" && t.firstPassFigureRefusal && t.finalVerifierOk && t.figuresChanged,
  },
  {
    // R4: a rejection after which the owner moved the thread to another scenario.
    // INERT TODAY, exactly as R6 is: `rerouted` is null in the loader because no owner-sourced
    // reroute record exists (the inbox has no "wrong scenario" action, and a reject reason is
    // optional). The next `routing.decided` is the router reading the customer's NEXT message,
    // never the owner's act, so it is not evidence (review 1 B1). TA for Kabir: decide whether
    // the inbox gets an owner reroute action that writes such a record. Until it exists,
    // `rerouted` stays null.
    id: "R4",
    stage: "routing",
    lesson: true,
    matches: (t) => t.status === "rejected" && t.rerouted !== null && t.rerouted.from !== t.rerouted.to,
  },
  {
    // R5: the owner removed text the AI-tell linter had recorded on the draft.
    id: "R5",
    stage: "gate",
    lesson: true,
    matches: (t) => t.status === "approved" && t.aiTellsRemoved.length > 0,
  },
  {
    // R6: a bounce on the run's lead card.
    id: "R6",
    stage: "research",
    lesson: true,
    matches: (t) => t.leadCardBounced,
  },
];

const stageOrder = (s: TraceStage): number => TRACE_STAGES.indexOf(s);

/**
 * The attribution of one decided draft. See the file header for the discipline.
 * An approval with a cosmetic or unedited kind (and no substantive flag)
 * yields `none`, which writes nothing.
 */
export function attributeCorrection(t: DraftTrace): CorrectionAttribution {
  if (
    t.status === "approved" &&
    t.ownerFlaggedSubstantive !== true &&
    (t.editKind === "cosmetic" || t.editKind === "unedited")
  ) {
    return { draftId: t.draftId, none: "no_correction" };
  }
  // M6 (review 1): an approval whose edit kind was never recorded is not read at all. Evidence
  // from R1 to R6 could be the product of an edit nobody classified, so no rule may fire on it.
  if (t.status === "approved" && t.editKind === null) {
    return { draftId: t.draftId, ambiguous: [], matched: [] };
  }
  const matched = ATTRIBUTION_RULES.filter((r) => r.matches(t));
  const only = matched.length === 1 ? matched[0] : undefined;
  if (only !== undefined) return { draftId: t.draftId, stage: only.stage, ruleId: only.id, lesson: only.lesson };
  const stages = [...new Set(matched.map((r) => r.stage))].sort((a, b) => stageOrder(a) - stageOrder(b));
  return { draftId: t.draftId, ambiguous: stages, matched: matched.map((r) => r.id) };
}

/**
 * The figure half of a trace, from the two texts and the active KB's facts.
 * `figuresChanged` is the F-05 figure check (`figuresDiffer`, the verifier's
 * wide reader plus bare numbers); `corrected` has one entry per figure the
 * approved text states that the draft did not, naming the KB facts that state
 * it (`verifierFigureSet`, the reader the verifier grounds against). Pure.
 */
export function figureCorrections(
  draftBody: string,
  approvedBody: string,
  kb: readonly { readonly id: string; readonly fact: string }[],
): { readonly figuresChanged: boolean; readonly corrected: readonly FigureCorrection[] } {
  const figuresChanged = figuresDiffer(draftBody, approvedBody);
  if (!figuresChanged) return { figuresChanged: false, corrected: [] };
  const before = verifierFigureSet(draftBody);
  const added = [...verifierFigureSet(approvedBody)].filter((f) => !before.has(f)).sort();
  const kbFigures = kb.map((c) => ({ id: c.id, figures: verifierFigureSet(c.fact) }));
  return {
    figuresChanged: true,
    corrected: added.map((f) => ({ kbFactIds: kbFigures.filter((c) => c.figures.has(f)).map((c) => c.id) })),
  };
}
